/**
 * @param {unknown} [value]
 * @returns {{ isPercentage: unknown, value: unknown }}
 */
function percentageParser(value = null) {
  return {
    isPercentage: value && Boolean(String(value).match(/%$/)),
    value: value && Number(String(value).replace(/%$/, '')),
  };
}

export const DATEFORMAT = 'yy-mm-dd';

export const BusinessRuleOperator = [
  'Equal',
  'GreaterThanOrEqual',
  'LessThanOrEqual',
];

export const BusinessRuleTypes = [
  'Basic',
  'Custom',
];

export const AllotmentRolesConstants = {
  ADMIN: 1,
  FINANCE: 2,
  TOUR_OPERATOR: 3,
  READONLY_ADMIN: 4,
  HCC_READONLY: 5,
};

export const TourOperatorRolesConstants = {
  ALL_ACCESS: 1,
  ONLY_REPORTING: 2,
  ONLY_ALLOTMENT_CHANGES: 3
};

/* mapping for short column titles and long for tooltip usage*/
export const titleMappings = {
  origin: {
    short: 'FROM',
    long: 'Origin',
  },
  destination: {
    short: 'TO',
    long: 'Destination',
  },
  flightNumber: {
    short: 'FLNR',
    long: 'Flight Number',
  },
  departureDate: {
    short: 'DATE',
    long: 'Flight date',
  },
  contractSize: {
    short: 'OR.SZ',
    long: 'Original Allotment size'
  },
  size: {
    short: 'CUR.SZ',
    long: 'Current size'
  },
  upgradeSize: {
    short: 'UPG.SZ',
    long: 'Upgrade size'
  },
  newSize: {
    short: 'NEW SZ',
    long: 'New size'
  },
  sold: {
    short: 'SOLD',
    long: 'Sold'
  },
  status: {
    short: 'STS',
    long: 'Status'
  },
  amadeusStatus: {
    short: 'STS',
    long: 'Amadeus Status'
  },
  dayOfWeek: {
    short: 'DAY',
    long: 'Day'
  },
  week: {
    short: 'WK',
    long: 'Week'
  },
  month: {
    short: 'MO',
    long: 'Month'
  },
  final: {
    short: 'FINAL',
    long: 'Final'
  },
  country: {
    short: 'CTRY',
    long: 'Country'
  },
  seasonId: {
    short: 'SEAS',
    long: 'Season'
  },
  fareBasisCodeId: {
    short: 'FBC',
    long: 'Fare basis code'
  },
  amadeusLastUpdated: {
    short: 'UPD',
    long: 'Amadeus updated'
  },
};

/* overwrites headerTitles with processHeaderCallback */
/** @param {{ column: { getColId: () => string, getColDef: () => { headerName?: string } } }} params */
export function getHeaderCallbackTitles(params) {
  switch (params.column.getColId()) {
    case 'origin':
      return 'From';
    case 'destination':
      return 'To';
    case 'operatingAirlineId':
      return 'OperatingAirline';
    case 'tourOperatorId':
      return 'TourOperator';
    case 'departureDate':
      return 'Flight Date';
    case 'contractSize':
      return 'Original Allotment size';
    case 'size':
      return 'Current size';
    case 'flightNumber':
      return 'Flight number';
    case 'netFare':
      return 'Net Fare';
    case 'fareBasisCodeId':
      return 'FBC';
    case 'fareBasisCodeInput':
      return 'FBC-Branding';
    case 'seasonId':
      return 'Season';
    case 'created':
      return 'Update';
    default:
      return params.column.getColDef().headerName ?? '';
  }
}

export const AllotmentRoles = Object.entries(AllotmentRolesConstants).map(([key, value]) => ({[value]: key}));

/**
 * @param {unknown} params
 * @returns {{ day?: number, date?: string, time?: string, timeWithoutSeconds?: string, timestamp?: string }}
 */
export function dateFormatter(params) {
  const value = params && typeof params === 'object' && 'value' in params ? params.value : params;
  const timestamp = new Date(/** @type {string | number | Date} */ (value));

  if (!timestamp || Number.isNaN(timestamp.getTime())) {
    return {};
  }

  const year = timestamp.getFullYear();
  const month = String(timestamp.getMonth() + 1).padStart(2, '0');
  const day = String(timestamp.getDate()).padStart(2, '0');
  const hours = String(timestamp.getHours()).padStart(2, '0');
  const minutes = String(timestamp.getMinutes()).padStart(2, '0');
  const seconds = String(timestamp.getSeconds()).padStart(2, '0');

  const dayOfWeek = timestamp.getDay() === 0 ? 7 : timestamp.getDay();

  return {
    day: dayOfWeek,
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}:${seconds}`,
    timeWithoutSeconds: `${hours}:${minutes}`,
    timestamp: `${year}${month}${day}${hours}${minutes}${seconds}`,
  };
};

/** @param {{ column?: { colId?: string, getColId?: () => string }, data?: Record<string, unknown>, value?: string | number }} params */
export function getPercentageFormatter(params) {
  const colId = params.column?.colId ?? params.column?.getColId?.();
  if (colId === 'decreaseAmount') {
    return `${params.value}${params.data?.decreaseIsPercentage ? '%' : '' }`;
  }
  if (colId === 'increaseAmount') {
    return `${params.value}${params.data?.increaseIsPercentage ? '%' : '' }`;
  }
  return params.value == null ? '' : String(params.value);
};

/**
 * @param {{ api: import('ag-grid-community').GridApi | null, prefix: string, type?: 'excel' | 'csv', options?: import('ag-grid-community').CsvExportParams | import('ag-grid-community').ExcelExportParams }} options
 */
export function exportFile({ api, prefix, type = 'excel', options = {}}) {
  if (!api) {
    throw new Error('Grid API is not ready for export');
  }

  const fileName = `${prefix}-${dateFormatter(new Date()).timestamp}`;

  const params = {
    suppressQuotes: true,
    columnSeparator: ';',
    ...options,
    fileName,
  };

  return type === 'excel'
    ? api.exportDataAsExcel(/** @type {import('ag-grid-community').ExcelExportParams} */ (params))
    : api.exportDataAsCsv(/** @type {import('ag-grid-community').CsvExportParams} */ (params));
}

/**
 * @param {{ data: Record<string, unknown>, column?: false | { colId: string } | null }} options
 * @returns {Record<string, unknown>}
 */
export function setBusinessRulesFormat({ data: params, column = undefined }) {
  // {
  //   "id": 8,
  //   "type": "Basic",
  //   "decreaseAmount": "10%",
  //   "decreaseIsPercentage": false,
  //   "increaseAmount": 10,
  //   "increaseIsPercentage": false,
  //   "daysAllowedBeforeFlight": 0,
  //   "origin": null,
  //   "destination": null,
  //   "flightNumber": null,
  //   "fromDate": null,
  //   "toDate": null,
  //   "increaseOperator": "GreaterThanOrEqual",
  //   "decreaseOperator": "LessThanOrEqual",
  //   "decreaseAllowedAfterIncrease": true,
  //   "tourOperatorId": "9",
  //   "seasonId": "2",
  //   "fareBasisCodeId": null
  // }
  const { decreaseAmount, decreaseAllowedAfterIncrease, increaseAmount, fromDate, toDate, flightNumberFrom, flightNumberTo } = params;
  const data = {
    ...params,
    ...( decreaseAmount && (column === false || (column != null && column.colId === 'decreaseAmount')) ? { decreaseIsPercentage: percentageParser(decreaseAmount).isPercentage } : {}),
    ...( decreaseAmount && (column === false || (column != null && column.colId === 'decreaseAmount')) ? { decreaseAmount: percentageParser(decreaseAmount).value } : {}),
    ...( decreaseAllowedAfterIncrease ? { decreaseAllowedAfterIncrease: JSON.parse(String(decreaseAllowedAfterIncrease)) } : {}),
    ...( increaseAmount && (column === false || (column != null && column.colId === 'increaseAmount')) ? { increaseIsPercentage: percentageParser(increaseAmount).isPercentage } : {}),
    ...( increaseAmount && (column === false || (column != null && column.colId === 'increaseAmount')) ? { increaseAmount: percentageParser(increaseAmount).value } : {}),
    ...( fromDate ? { fromDate: dateFormatter(fromDate).date } : {}),
    ...( toDate ? { toDate: dateFormatter(toDate).date } : {}),
    ...( flightNumberFrom ? { flightNumberFrom: Number(flightNumberFrom) } : {}),
    ...( flightNumberTo ? { flightNumberTo: Number(flightNumberTo) } : {})
  };

  return data;
};

import GridButton from '@/components/Grid/GridButton.vue';
import GridDataFilter from '@/components/Grid/GridDataFilter.vue';
import GridDataPercentage from '@/components/Grid/GridDataPercentage.vue';
import GridDataSuccess from '@/components/Grid/GridDataSuccess.vue';
import GridDatePicker from '@/components/Grid/GridDatePicker.vue';
import { GridFilterDate } from '@/components/Grid/GridFilters';

export const GridCheckbox = {
  filter: false,
  headerName: '',
  maxWidth: 50,
  resizable: false,
  sortable: false,
  suppressAutoSize: true,
  suppressMovable: true,
  suppressSizeToFit: true,
  width: 55,
};

/**
 * @param {{ tourOperators?: Record<string, string> | null, seasons?: Record<string, string> | null, BusinessRuleTypes?: string[], BusinessRuleOperator?: string[], isActiveMappings?: Record<string, string> | null, airportsList?: unknown[], fareBasisCodes?: Record<string, string> | null }} vm
 * @param {{ isTourOperator: boolean }} options
 */
export function getBusinessRulesTestsTableDefinition(vm, { isTourOperator }) {
  return [
    /*
    daysAllowedBeforeFlight: 100
    decreaseAllowedAfterIncrease: true
    decreaseAmount: 100
    decreaseIsPercentage: true
    decreaseOperator: "Equal"
    destination: "str"
    fareBasisCodeId: 1
    flightNumber: "SN260"
    fromDate: "2023-05-08"
    id: 4
    increaseAmount: 100
    increaseIsPercentage: true
    increaseOperator: "Equal"
    origin: "str"
    seasonId: 1
    toDate: null
    tourOperatorId: 1
    type: "Custom"
     */
    {
      headerName: 'Rule id',
      field: 'id',
      width: 50,
    },
    {
      headerName: 'Client',
      field: 'tourOperatorId',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: Object.keys(vm.tourOperators || {}),
      },
      refData: vm.tourOperators ?? undefined,
      hide: isTourOperator,
    },
    {
      headerName: 'Reason',
      field: 'ruleReason',
      editable: false
    },
    {
      headerName: 'Season',
      field: 'seasonId',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: Object.keys(vm.seasons || {}),
      },
      refData: vm.seasons ?? undefined,
    },
    {
      headerName: 'Type',
      field: 'type',
      editable: false,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: vm.BusinessRuleTypes ?? BusinessRuleTypes,
      },
      cellClass: 'ag-cell-disabled',
    },
    {
      headerName: 'Decrease Operator',
      field: 'decreaseOperator',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: vm.BusinessRuleOperator ?? BusinessRuleOperator,
      },
    },
    {
      headerName: 'Decrease (% or seats)',
      field: 'decreaseAmount',
      valueFormatter: getPercentageFormatter,
      cellRenderer: GridDataPercentage,
    },
    {
      headerName: 'Increase Operator',
      field: 'increaseOperator',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: vm.BusinessRuleOperator ?? BusinessRuleOperator,
      },
    },
    {
      headerName: 'Increase (% or seats)',
      field: 'increaseAmount',
      valueFormatter: getPercentageFormatter,
      cellRenderer: GridDataPercentage,
    },
    {
      headerName: 'Last change',
      field: 'daysAllowedBeforeFlight',
    },
    {
      headerName: 'Decrease after increase',
      field: 'decreaseAllowedAfterIncrease',
      cellRenderer: GridButton,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: Object.keys(vm.isActiveMappings || {}),
      },
      refData: vm.isActiveMappings ?? undefined,
      cellRendererParams: /** @param {{ value: boolean }} params */ params => ({
        className: params.value && 'text-green' || 'text-grey',
        label: params.value && 'Disable' || 'Enable',
        icon: params.value && 'toggle_on' || 'toggle_off',
        clicked: function() {}
      }),
    },
    {
      headerName: 'Fallback to previous',
      field: 'isFallbackToPrevious',
      cellRenderer: GridButton,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: Object.keys(vm.isActiveMappings || {}),
      },
      refData: vm.isActiveMappings ?? undefined,
      cellRendererParams: /** @param {{ value: boolean }} params */ params => ({
        className: params.value && 'text-green' || 'text-grey',
        label: params.value && 'Disable' || 'Enable',
        icon: params.value && 'toggle_on' || 'toggle_off',
        clicked: function() {}
      }),
    },
    {
      headerName: 'Origin',
      field: 'origin',
      editable: /** @param {{ data: { type: string } }} params */ params => params.data.type === 'Custom',
      cellClass: /** @param {{ data: { type: string } }} params */ params => params.data.type !== 'Custom' ? 'ag-cell-disabled' : '',
      cellEditor: GridDataFilter,
      cellEditorParams: {
        options: vm.airportsList,
        key: 'iata',
      },
      cellEditorPopup: true,
    },
    {
      headerName: 'Destination',
      field: 'destination',
      editable: /** @param {{ data: { type: string } }} params */ params => params.data.type === 'Custom',
      cellClass: /** @param {{ data: { type: string } }} params */ params => params.data.type !== 'Custom' ? 'ag-cell-disabled' : '',
      cellEditor: GridDataFilter,
      cellEditorParams: {
        options: vm.airportsList,
        key: 'iata',
      },
      cellEditorPopup: true,
    },
    {
      headerName: 'Flight number',
      field: 'flightNumber',
      editable: /** @param {{ data: { type: string } }} params */ params => params.data.type === 'Custom',
      cellClass: /** @param {{ data: { type: string } }} params */ params => params.data.type !== 'Custom' ? 'ag-cell-disabled' : '',
      valueGetter: /** @param {{ data?: { airline?: string, flightNumberFrom?: string | number, flightNumberTo?: string | number } }} params */ params => `${params?.data?.airline || ''}${params?.data?.flightNumberFrom || ''}${params?.data?.flightNumberTo ? `-${params?.data?.flightNumberTo}` : ''}`,
    },
    {
      headerName: 'From',
      field: 'fromDate',
      editable: /** @param {{ data: { type: string } }} params */ params => params.data.type === 'Custom',
      cellClass: /** @param {{ data: { type: string } }} params */ params => params.data.type !== 'Custom' ? 'ag-cell-disabled' : '',
      cellEditor: GridDatePicker,
      cellEditorPopup: false,
      ...GridFilterDate,
    },
    {
      headerName: 'Until',
      field: 'toDate',
      editable: /** @param {{ data: { type: string } }} params */ params => params.data.type === 'Custom',
      cellClass: /** @param {{ data: { type: string } }} params */ params => params.data.type !== 'Custom' ? 'ag-cell-disabled' : '',
      cellEditor: GridDatePicker,
      cellEditorPopup: false,
      ...GridFilterDate,
    },
    {
      headerName: 'Fare Basis Code',
      field: 'fareBasisCodeId',
      editable: /** @param {{ data: { type: string } }} params */ params => params.data.type === 'Custom',
      cellClass: /** @param {{ data: { type: string } }} params */ params => params.data.type !== 'Custom' ? 'ag-cell-disabled' : '',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: Object.keys(vm.fareBasisCodes || {}),
      },
      refData: vm.fareBasisCodes ?? undefined,
    },
    {
      headerName: 'Result',
      field: 'result',
      cellStyle: {
        alignItems: 'center',
        display: 'flex',
      },
      cellRenderer: GridDataSuccess,
      cellRendererParams: /** @param {{ data: { matchingRuleIds: number[], id: string | number } }} params */ params => {
        return ({
          passed: params.data.matchingRuleIds.includes(Number(params.data.id)),
        });
      },
    }
  ];
}
